import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { merge, Observable, of, OperatorFunction, Subject } from 'rxjs';
import {
  delay,
  filter,
  map,
  mergeMap,
  repeat,
  switchMap,
  take,
  takeUntil,
  tap,
  timeout,
  withLatestFrom,
} from 'rxjs/operators';

import {
  FeatureNames,
  GatewayRemoteDiagnosticsStatus,
  IsStringValueProperty,
  RemoteDiagnosticsCommands,
  RemoteDiagnosticsProperties,
} from '@vi-iot/client';

import { RemoteDiagnosticsBackgroundService } from '@services';
import {
  DIAGNOSTICS_CONNECTION_STATUS,
  DIAGNOSTICS_CONNECTION_STEP,
  establishingDiagnosticsConnectionTimeout,
  FeatureService,
  isReady,
  RemoteDiagnosticsActions,
  RemoteDiagnosticsStore,
  InstallationDetailsStore,
  AuthStore,
  RemoteDiagnosticsService,
  InstallationFeaturesStore,
  DevicesStore,
} from '@store';
import { ActivatedRoute } from '@angular/router';
import { AppInsightsMonitoringService } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP } from 'src/app/shared/services/app-insights/events/remote-diagnostics.events';
import { LogLevelEnum } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { SUFFIX } from '../../../shared/common/units';

import { environment } from '../../../../environments/environment';
import { DiagnosticsConnectionModalComponent } from './diagnostics-connection-modal/diagnostics-connection-modal.component';

@Component({
  selector: 'app-diagnostics-connection-box',
  templateUrl: './diagnostics-connection-box.component.html',
  styleUrls: ['./diagnostics-connection-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticsConnectionBoxComponent implements OnDestroy, OnInit {
  isRsaUserWithWriteRights: boolean;
  diagnosticsDialogRef: MatDialogRef<DiagnosticsConnectionModalComponent>;
  connectionProcessInPercent: string;
  isSelectedGatewayACascadeAndDeviceNotSelected: boolean;
  selectedDeviceSerial: string;
  highlightConnectionButton: boolean;
  readonly diagnosticsConnectionStatus = DIAGNOSTICS_CONNECTION_STATUS;
  readonly connectionSteps = [
    DIAGNOSTICS_CONNECTION_STEP.GET_REASON_TO_CONNECT,
    DIAGNOSTICS_CONNECTION_STEP.WARNING,
    DIAGNOSTICS_CONNECTION_STEP.LOGIN,
    DIAGNOSTICS_CONNECTION_STEP.OPEN_SESSION,
    DIAGNOSTICS_CONNECTION_STEP.REMOTE_VCI_REGISTER,
    DIAGNOSTICS_CONNECTION_STEP.REMOTE_VCI_AVAILABILITY_CHECK,
    DIAGNOSTICS_CONNECTION_STEP.REMOTE_VCI_ESTABLISHING_CONNECTION,
    DIAGNOSTICS_CONNECTION_STEP.SETUP_INFO,
    DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_OPEN_ATTEMPT,
    DIAGNOSTICS_CONNECTION_STEP.PRODUCT_MATRIX_FETCH,
    DIAGNOSTICS_CONNECTION_STEP.DEVICE_MAIN_LOGICAL_LINK_OPEN_ATTEMPT,
    DIAGNOSTICS_CONNECTION_STEP.SCAN_TOPOLOGY,
    DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_CLOSE_ATTEMPT,
    DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_FROM_TOPOLOGY_OPEN_ATTEMPT,
    DIAGNOSTICS_CONNECTION_STEP.ACTIVE_DTC_FETCH,
    DIAGNOSTICS_CONNECTION_STEP.CONNECTION_ACTIVE,
  ];

  @Input() gatewaySerial: string;
  @Input() connectionStatus: DIAGNOSTICS_CONNECTION_STATUS;
  @Input() isDemo: boolean;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  private stopPollingFeatureGatewayRemoteDiagnostics$: Subject<void> = new Subject<void>();

  constructor(
    public remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private dialog: MatDialog,
    private remoteDiagnosticsBackgroundService: RemoteDiagnosticsBackgroundService,
    private featureService: FeatureService,
    private installationFeaturesStore: InstallationFeaturesStore,
    private cdr: ChangeDetectorRef,
    private actions$: Actions,
    private appInsights: AppInsightsMonitoringService,
    private installationDetailsStore: InstallationDetailsStore,
    private activatedRoute: ActivatedRoute,
    private authStore: AuthStore,
    private remoteDiagnosticsService: RemoteDiagnosticsService,
    private devicesStore: DevicesStore
  ) {}

  ngOnInit(): void {
    this.authStore
      .isRsaUserWithWriteRights()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        (isRsaUserWithWriteRights) => (this.isRsaUserWithWriteRights = isRsaUserWithWriteRights)
      );

    this.remoteDiagnosticsStore
      .getConnectionStatus()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((status) => {
        switch (status) {
          case DIAGNOSTICS_CONNECTION_STATUS.FAILED:
            this.appInsights.logEvent(
              APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.CONNECTION_STATUS_FAILED,
              LogLevelEnum.ERROR
            );
            break;
          case DIAGNOSTICS_CONNECTION_STATUS.ACTIVE:
            this.appInsights.logEvent(
              APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.CONNECTION_STATUS_CONNECTED,
              LogLevelEnum.ERROR
            );
            break;
          default:
            break;
        }
      });

    this.activatedRoute.queryParams
      .pipe(
        withLatestFrom(this.installationDetailsStore.isSelectedGatewayACascade$),
        switchMap(([queryParams, isSelectedGatewayACascade]) =>
          this.devicesStore.selectDeviceIdByDeviceSerial(queryParams['deviceSerial']).pipe(
            take(1),
            map((selectedDeviceId) => [selectedDeviceId, isSelectedGatewayACascade])
          )
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(([selectedDeviceId, isSelectedGatewayACascade]) => {
        this.installationFeaturesStore
          .selectInstallationDeviceFeature<IsStringValueProperty, never>(
            FeatureNames.deviceSerial,
            selectedDeviceId?.toString()
          )
          .pipe(take(1))
          // here use device.Serial feature and not vnext form url
          .subscribe((serial) => (this.selectedDeviceSerial = serial?.properties?.value?.value));

        this.isSelectedGatewayACascadeAndDeviceNotSelected =
          isSelectedGatewayACascade && !this.selectedDeviceSerial;
        this.cdr.markForCheck();
      });

    this.remoteDiagnosticsService.scrollToTopValue$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(() => {
        this.highlightConnectionButton = true;
        this.cdr.markForCheck();
        setTimeout(() => {
          this.highlightConnectionButton = false;
          this.cdr.markForCheck();
        }, 3000);
      });
  }

  openDiagnosticsConnectionModal(): void {
    if (this.isRsaUserWithWriteRights) {
      this.remoteDiagnosticsStore.setActiveConnectionStep(
        DIAGNOSTICS_CONNECTION_STEP.GET_REASON_TO_CONNECT
      );
    } else {
      this.remoteDiagnosticsStore.setActiveConnectionStep(DIAGNOSTICS_CONNECTION_STEP.WARNING);
    }

    this.diagnosticsDialogRef = this.dialog.open(DiagnosticsConnectionModalComponent, {
      width: '535px',
      disableClose: false,
      autoFocus: false,
      data: {
        gatewaySerial: this.gatewaySerial,
        deviceSerial: this.selectedDeviceSerial,
      },
    });

    this.diagnosticsDialogRef.afterClosed().subscribe((step?: DIAGNOSTICS_CONNECTION_STEP) => {
      this.appInsights.logEvent(
        APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.START_CONNECTION_SEQUENCE,
        LogLevelEnum.ERROR
      );

      if (step === DIAGNOSTICS_CONNECTION_STEP.CONNECTION_ESTABLISHING) {
        this.remoteDiagnosticsStore.findDiagStackInstance(
          this.gatewaySerial,
          this.selectedDeviceSerial
        );

        this.pollFeatureGatewayRemoteDiagnostics();
        this.installationFeaturesStore
          .selectInstallationGatewayFeature<RemoteDiagnosticsProperties, RemoteDiagnosticsCommands>(
            FeatureNames.gatewayRemoteDiagnostics
          )
          .pipe(
            tap((feature) => {
              if (!this.isDemo) {
                this.appInsights.logEvent(
                  APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.VALUE_OF_GATEWAY_REMOTE_DIAGNOSTICS_FEATURE,
                  LogLevelEnum.TRACE,
                  { feature }
                );
              }
            }),
            filter((feature) => isReady(feature)),
            filter(
              (feature) => feature.properties.status.value === GatewayRemoteDiagnosticsStatus.Ready
            ),
            timeout(establishingDiagnosticsConnectionTimeout),
            this.delayValueIfDemo(),
            take(1),
            takeUntil(this.ngUnsubscribe$)
          )
          .subscribe(
            (feature) => {
              this.remoteDiagnosticsStore.registerVci(
                this.gatewaySerial,
                feature.properties.webSocketUrl.value
              );
            },
            () => {
              this.stopPollingFeatureGatewayRemoteDiagnostics();
              this.remoteDiagnosticsStore.triggerDisconnectSequence(this.gatewaySerial);
              this.remoteDiagnosticsStore.setConnectionStatus(DIAGNOSTICS_CONNECTION_STATUS.FAILED);
              this.remoteDiagnosticsStore.setActiveConnectionStep(
                DIAGNOSTICS_CONNECTION_STEP.WARNING
              );
            }
          );

        this.subscribeToEstablishingConnectionStep();
      }
    });
  }

  subscribeToEstablishingConnectionStep(): void {
    this.remoteDiagnosticsStore.getActiveConnectionStep$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((step) => {
        const steps = this.connectionSteps.length;
        const establishingStep = this.connectionSteps.indexOf(step);
        if (establishingStep === -1) {
          if (environment.env !== 'production') {
            console.log(`connectionSteps does not contain ${step}`);
          }
        }

        this.connectionProcessInPercent = `${Math.round((100 * establishingStep) / steps)}${
          SUFFIX.percentage
        }`;
        this.cdr.detectChanges();

        switch (step) {
          case DIAGNOSTICS_CONNECTION_STEP.CONNECTION_ACTIVE:
          case DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_FROM_TOPOLOGY_OPEN_ATTEMPT:
            this.remoteDiagnosticsBackgroundService.initLogicalLinkListInterval(this.gatewaySerial);
            break;
          default:
            break;
        }
      });
  }

  private delayValueIfDemo<T>(): OperatorFunction<T, T> {
    // In demo mode it's difficult to modify feature data
    // its value is mocked and delivered earlier than expected thus handle only one emission with delay

    return (inputObservable: Observable<T>): Observable<T> =>
      this.isDemo
        ? inputObservable.pipe(
            delay(500),
            tap(() => {
              this.installationFeaturesStore.getMockedFeature(
                'remoteDiagnosticsActive',
                FeatureNames.gatewayRemoteDiagnostics
              );
            })
          )
        : inputObservable;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private stopPollingFeatureGatewayRemoteDiagnostics(): void {
    this.stopPollingFeatureGatewayRemoteDiagnostics$.next();
    this.stopPollingFeatureGatewayRemoteDiagnostics$.complete();
  }

  private pollFeatureGatewayRemoteDiagnostics(): void {
    this.appInsights.logEvent(
      APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.START_POLLING_GATEWAY_REMOTE_DIAGNOSTICS_FEATURE,
      LogLevelEnum.TRACE
    );
    of({})
      .pipe(
        mergeMap(() =>
          of(this.featureService.fetchGatewayFeature(FeatureNames.gatewayRemoteDiagnostics))
        ),
        delay(5000),
        repeat(),
        takeUntil(
          merge(
            this.actions$.pipe(ofType(RemoteDiagnosticsActions.diagstackGetFreeInstance.failed)),
            this.actions$.pipe(ofType(RemoteDiagnosticsActions.login.failed)),
            this.actions$.pipe(ofType(RemoteDiagnosticsActions.remoteVciRegister.failed)),
            this.actions$.pipe(ofType(RemoteDiagnosticsActions.remoteVciAvailabilityCheck.failed)),
            this.actions$.pipe(ofType(RemoteDiagnosticsActions.vciConnect.failed)),
            this.actions$.pipe(ofType(RemoteDiagnosticsActions.mainLogicalLinkOpen.failed)),
            this.actions$.pipe(ofType(RemoteDiagnosticsActions.logicalLinkOpen.failed)),
            this.actions$.pipe(ofType(RemoteDiagnosticsActions.fetchDeviceTopology.failed)),
            this.actions$.pipe(ofType(RemoteDiagnosticsActions.fetchDeviceTopology.success)),
            this.stopPollingFeatureGatewayRemoteDiagnostics$,
            this.ngUnsubscribe$
          )
        )
      )
      .subscribe();
  }
}
